import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Grid,
  Select,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowBack,
  IconFileText,
  IconPackages,
  IconPrinter,
  IconReceipt2,
  IconUserCircle,
} from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import { getValueById } from "../../../_base/helper/FunctionHelper";
import { MessageResponse } from "../../../model/MessageResponse";
import { TblDmWarrantyReceiveTicket } from "../../../model/TblDmWarrantyReceiveTicket";
import { getItemPositionSelect } from "../../../service/getSelectApi";
import GoodsReceiptInspectionRecord from "../ReceiveWarranty/GoodsReceiptInspectionRecord";
import PrinterReturnCustomers from "../ReceiveWarranty/PrinterReturnCustomers";
import PrinterReceiveTicket from "./PrinterReceiveTicket";
import ProductWarrantyList from "./ProductWarrantyList";
import { PageHeader, SectionCard } from "./ReceiveTicketUI";

// Các mẫu in, mỗi thời điểm chỉ mount 1 mẫu để lấy ref truyền cho useReactToPrint
const PRINT_VIEW = {
  RECEIVE_TICKET: 1,
  RETURN_CUSTOMER: 2,
  INSPECTION_RECORD: 3,
};

// Chỉ phiếu đã trả khách (toàn bộ / một phần) mới in được phiếu xuất trả khách
const STATUS_RETURNED = ["DA_TRA_KHACH", "TRA_MOT_PHAN"];

// Nhãn + màu hiển thị trạng thái phiếu trên tiêu đề, khớp với màn danh sách
const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DA_NHAP: { label: "Đã nhập kho", color: "green" },
  DA_TRA_KHACH: { label: "Đã trả khách", color: "teal" },
  TRA_MOT_PHAN: { label: "Trả một phần", color: "orange" },
  DA_TIEP_NHAN: { label: "Đã tiếp nhận", color: "blue" },
};

const PRINT_PAGE_STYLE = `
  @page {
    size: auto;
    margin: 5mm 0;
  }`;

// Dữ liệu API detailV2 trả về, dùng để hiển thị và truyền sang các mẫu in
type ReceiveTicketDetailValues = TblDmWarrantyReceiveTicket & {
  createDate?: string | null;
  custPhone?: string | null;
  nameCustomerGroup?: string | null;
  tblWarrantyReceiveTicketDetailModels?: any[] | null;
};

// Danh mục dùng để tra cứu tên hiển thị theo id (getValueById)
type LookupItem = { id: any; name: string };

const initialFormValues: ReceiveTicketDetailValues = {
  id: "0",
  empId: null,
  requestId: null,
  errorNumber: null,
  dateReceive: new Date().toString(),
  inventoryCode: null,
  note: null,
  idItemPosition: null,
  branchId: null,
  idCustomerGroup: null,
  inventoryId: null,
  tblWarrantyReceiveTicketDetailCommands: null,
};

// Địa chỉ chi nhánh quá dài thì bỏ bớt "Phường/Quận/Thành phố" cho gọn khi in
const formatBranchAddress = (address: any) =>
  String(address).length > 52
    ? String(address)
        .replace(/\b(Phường|Quận|Thành phố)\b/gi, "")
        .replace(/\s+/g, " ")
        .trim()
    : address;

const DetailDataView = ({
  id: idProp,
  onClose: onCloseProp,
  actionView: actionViewProp,
}: DetailReceiptFormProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Khi mở bằng navigate thì id/code được truyền qua location.state,
  // mở ở tab mới (Ctrl + click) thì fallback sang query string
  const id = idProp ?? (location.state as any)?.id ?? searchParams.get("id");
  const code = (location.state as any)?.code ?? searchParams.get("code");
  const actionView = actionViewProp ?? "DETAIL";
  // Nếu không có onClose (mở bằng route) thì quay lại trang trước
  const onClose = onCloseProp ?? (() => navigate(-1));

  const form = useForm<ReceiveTicketDetailValues>({
    mode: "uncontrolled",
    validateInputOnChange: true,
    initialValues: { ...initialFormValues },
  });

  const [dataRequestFormSelected, setDataRequestFormSelected] = useState<any>();
  const [dataEmployeeDetail, setDataEmployeeDetail] = useState<any>();
  const [dataInventorySelect, setDataInventorySelect] = useState<
    ComboboxItem[]
  >([]);
  const [dataItemPositionSelect, setDataItemPositionSelect] = useState<
    ComboboxItem[]
  >([]);
  const [dataDepartment, setDataDepartment] = useState<LookupItem[]>([]);
  const [dataRole, setDataRole] = useState<LookupItem[]>([]);
  const [addressBranch, setAddressBranch] = useState<any>({
    addressBranch: "",
    nameBranch: "",
    companyCode: "",
  });
  const [dataPrint, setDataPrint] = useState<any>(null);
  const [printView, setPrintView] = useState<number | null>(null);
  const [editAndSave, setEditAndSave] = useState(false);
  const [isEdit, setIsEdit] = useState({ note: false });
  const [visible, { close, open }] = useDisclosure(false);

  const componentRef = React.useRef(null);
  const componentRefRC = React.useRef(null);
  const componentRefGRIR = React.useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: PRINT_PAGE_STYLE,
  });
  const handlePrintRC = useReactToPrint({
    contentRef: componentRefRC,
    pageStyle: PRINT_PAGE_STYLE,
  });
  const handlePrintGRIR = useReactToPrint({
    contentRef: componentRefGRIR,
    pageStyle: PRINT_PAGE_STYLE,
  });

  const { requestId, inventoryCode, attribute6 } = form.getValues();
  const warrantyReceiveTicketDetail =
    form.getValues().tblWarrantyReceiveTicketDetailCommands ?? [];
  const receiveTicketDetailModels =
    form.getValues().tblWarrantyReceiveTicketDetailModels ?? [];

  const buildDetailUrl = (isPrint?: boolean) =>
    `/api/v1/TblWarrantyReceiveTicket/detailV2?${
      code ? `code=${code}` : `id=${id}`
    }${isPrint ? "&isPrint=true" : ""}`;

  const getDataReceiveTicket = async () => {
    open();
    try {
      const res =
        await repositoryMdm.get<MessageResponse<any>>(buildDetailUrl());

      if (!res?.success) {
        onClose("");
        return;
      }

      if (!res.data) return;

      form.setValues(res.data);
      getAddressBranch(res.data.branchId);
    } catch (error) {
      console.error(error);
    } finally {
      close();
    }
  };

  // Phiếu xuất trả khách cần dữ liệu riêng (isPrint=true) nên lấy khi bấm in
  const getDataPrintReturnCustomer = async () => {
    open();
    try {
      const res = await repositoryMdm.get<MessageResponse<any>>(
        buildDetailUrl(true),
      );

      if (!res?.success) {
        onClose("");
        return;
      }

      if (!res.data) return;

      setDataPrint(res.data);
      setPrintView(PRINT_VIEW.RETURN_CUSTOMER);
    } catch (error) {
      console.error(error);
    } finally {
      close();
    }
  };

  const getAddressBranch = async (branchId: any) => {
    const res = await repositoryDelivery.get<MessageResponse<any>>(
      "/api/v1/TblDmMasterOrg/get-all",
    );

    if (!res?.success) return;

    const branch = res.data?.find(
      (item: any) => Number(item.id) === Number(branchId),
    );

    if (!branch) return;

    setAddressBranch({
      addressBranch: formatBranchAddress(branch.address),
      nameBranch: branch.name,
      companyCode: branch.companyCode,
    });
  };

  const getDetailRequestForm = async () => {
    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/TblWarrantyServiceRequestForm/detail?id=${requestId}`,
    );

    setDataRequestFormSelected(res?.success ? res.data : undefined);
  };

  const getInventorySelect = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      `/api/v1/TblDmInventory/get-select-by-branch?branchId=${dataRequestFormSelected?.branchId}`,
    );

    if (res?.success) {
      setDataInventorySelect(
        res.data
          ?.filter(
            (item: any) =>
              item.inventoryId != null && item.inventoryName != null,
          )
          ?.map((item: any) => ({
            value: item.inventoryCode?.toString(),
            label: `${item.inventoryName} - ${item.inventoryCode}`,
          })),
      );
    }
  };

  const getDataItemPosition = async () => {
    const res = await getItemPositionSelect(inventoryCode);

    setDataItemPositionSelect(
      res
        ?.filter((item: any) => item.id != null && item.positionName != null)
        ?.map((item: any) => ({
          value: item.id?.toString(),
          label: item.positionName,
        })) ?? [],
    );
  };

  const getDataDepartment = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblDepartment/get-all",
    );

    if (res?.success) {
      setDataDepartment(
        res.data
          ?.filter((item: any) => item.name != null)
          ?.map((item: any) => ({ id: item.departmentId, name: item.name })),
      );
    }
  };

  const getDataRole = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblRole/get-all",
    );

    if (res?.success) {
      setDataRole(
        res.data
          ?.filter((item: any) => item.name != null)
          ?.map((item: any) => ({ id: item.id, name: item.name })),
      );
    }
  };

  const getEmployeeDetail = async () => {
    if (!dataRequestFormSelected?.empId) {
      setDataEmployeeDetail(undefined);
      return;
    }

    const res = await repositoryPos.get<MessageResponse<any>>(
      `/api/v1/TblDmEmployee/detail?id=${dataRequestFormSelected?.empId}`,
    );

    if (res?.success) {
      setDataEmployeeDetail(res.data);
    }
  };

  const handleChangeValue = (key: string, value: any, index: number) => {
    form.setValues((prevData) => {
      const prevDetails: any[] = Array.isArray(
        prevData.tblWarrantyReceiveTicketDetailCommands,
      )
        ? prevData.tblWarrantyReceiveTicketDetailCommands
        : [];

      if (prevDetails[index]?.[key] === value) {
        return prevData;
      }

      const updatedDetails = [...prevDetails];
      updatedDetails[index] = { ...updatedDetails[index], [key]: value };

      return {
        ...prevData,
        tblWarrantyReceiveTicketDetailCommands: updatedDetails,
      };
    });
  };

  const resetEditState = () => {
    setIsEdit({ note: false });
    setEditAndSave(false);
  };

  useEffect(() => {
    getDataDepartment();
    getDataRole();
  }, []);

  useEffect(() => {
    if (id || code) {
      getDataReceiveTicket();
    } else {
      onClose("");
    }
  }, [id, code]);

  useEffect(() => {
    if (requestId) {
      getDetailRequestForm();
    } else {
      setDataRequestFormSelected(undefined);
    }
  }, [requestId]);

  useEffect(() => {
    if (dataRequestFormSelected?.branchId) {
      getInventorySelect();
    } else {
      setDataInventorySelect([]);
    }
  }, [dataRequestFormSelected?.branchId]);

  useEffect(() => {
    getEmployeeDetail();
  }, [dataRequestFormSelected?.empId]);

  useEffect(() => {
    if (inventoryCode) {
      getDataItemPosition();
    } else {
      setDataItemPositionSelect([]);
    }
  }, [inventoryCode]);

  return (
    <>
      <Box
        component="form"
        mx="auto"
        p={"0px 10px"}
        onMouseDown={resetEditState}
        style={{ position: "relative" }}
      >
        <PageHeader
          icon={<IconFileText size={19} stroke={1.6} />}
          title="Chi tiết phiếu nhận hàng lỗi"
          subtitle={form.getValues().errorNumber ?? "—"}
          actions={
            <>
              {attribute6 && STATUS_LABELS[attribute6] && (
                <Badge
                  color={STATUS_LABELS[attribute6].color}
                  variant="light"
                  radius="sm"
                  size="lg"
                  tt="none"
                >
                  {STATUS_LABELS[attribute6].label}
                </Badge>
              )}
              <Button
                type="button"
                color="red"
                variant="light"
                radius="md"
                onClick={() => onClose("")}
                leftSection={!visible ? <IconArrowBack size={18} /> : undefined}
              >
                Thoát
              </Button>
              <Button
                color="blue"
                variant="light"
                radius="md"
                leftSection={<IconPrinter size={18} />}
                onClick={() => {
                  handlePrintGRIR();
                  setPrintView(PRINT_VIEW.INSPECTION_RECORD);
                }}
              >
                In biên bản kiểm tra tiếp nhận sản phẩm
              </Button>
              {STATUS_RETURNED.includes(attribute6 ?? "") && (
                <Button
                  color="blue"
                  variant="light"
                  radius="md"
                  leftSection={<IconPrinter size={18} />}
                  onClick={() => {
                    handlePrintRC();
                    if (!dataPrint) {
                      getDataPrintReturnCustomer();
                    } else {
                      setPrintView(PRINT_VIEW.RETURN_CUSTOMER);
                    }
                  }}
                >
                  In phiếu xuất trả khách
                </Button>
              )}
              <Button
                color={"blue"}
                variant="filled"
                radius="md"
                leftSection={<IconPrinter size={18} />}
                onClick={() => {
                  handlePrint();
                  setPrintView(PRINT_VIEW.RECEIVE_TICKET);
                }}
              >
                In phiếu tiếp nhận hàng lỗi
              </Button>
            </>
          }
        />
        <Grid gutter={"xs"}>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <SectionCard
              icon={<IconUserCircle size={15} stroke={1.7} />}
              title="Thông tin khách hàng hẹn gặp"
              color="blue"
            >
              <Grid>
                <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
                  <TextInput
                    label="Tên khách hàng"
                    placeholder="Tìm kiếm thông qua SĐT"
                    value={dataRequestFormSelected?.custMeeting ?? ""}
                    readOnly
                    variant="filled"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
                  <TextInput
                    label="Số điện thoại"
                    placeholder="Số điện thoại"
                    value={form.getValues().custPhone ?? ""}
                    readOnly
                    variant="filled"
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
                  <TextInput
                    label="Nhóm khách hàng"
                    placeholder="Tìm kiếm thông qua SĐT"
                    value={form.getValues().nameCustomerGroup ?? ""}
                    readOnly
                    variant="filled"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
                  <TextInput
                    label="Kênh mua hàng"
                    placeholder="Chọn kênh mua hàng"
                    value={dataRequestFormSelected?.channelName ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    label="Địa chỉ"
                    placeholder="Tìm kiếm thông qua SĐT"
                    value={[
                      dataRequestFormSelected?.additionalAddress,
                      dataRequestFormSelected?.meetingAddress,
                    ]
                      .filter((item) => item && String(item).length > 0)
                      .join(" - ")}
                    readOnly
                    variant="filled"
                  />
                </Grid.Col>
              </Grid>
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 8 }}>
            <SectionCard
              icon={<IconReceipt2 size={15} stroke={1.7} />}
              title="Thông tin phiếu tiếp nhận hàng lỗi"
              color="cyan"
            >
              <Grid>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Số phiếu tiếp nhận hàng lỗi"
                    value={form.getValues().errorNumber ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Mã phiếu tiếp nhận yêu cầu"
                    placeholder="Mã phiếu tiếp nhận yêu cầu"
                    value={dataRequestFormSelected?.requestNum ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Số phiếu tiếp nhận yêu cầu"
                    placeholder="Số phiếu tiếp nhận yêu cầu"
                    value={dataRequestFormSelected?.attribute10 ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <DateTimePicker
                    label="Ngày tạo phiếu"
                    valueFormat="DD-MM-YYYY hh:mm A"
                    value={
                      form.getValues().createDate
                        ? new Date(form.getValues().createDate ?? "")
                        : null
                    }
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Hình thức xử lý"
                    placeholder="Hình thức xử lý"
                    value={dataRequestFormSelected?.processingMethodName ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Nhân viên tiếp nhận"
                    placeholder="Nhập tên nhân viên tiếp nhận"
                    value={dataRequestFormSelected?.empnameincharge ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Chức danh"
                    placeholder="Chức danh của nhân viên tiếp nhận"
                    value={getValueById(
                      dataEmployeeDetail?.roleId,
                      dataRole,
                      "name",
                    )}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label="Bộ phận/Phòng bạn"
                    placeholder="Bộ phận/Phòng ban của nhân viên tiếp nhận"
                    value={getValueById(
                      dataEmployeeDetail?.departmentId,
                      dataDepartment,
                      "name",
                    )}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                  <Select
                    label="Kho"
                    placeholder="Nhập mã hoặc tên kho"
                    data={dataInventorySelect}
                    value={form.getValues().inventoryCode?.toString() ?? null}
                    comboboxProps={{ width: 325 }}
                    nothingFoundMessage="Không tìm thấy dữ liệu !"
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                  <Select
                    label="Vị trí kho"
                    placeholder="Nhập vị trí kho"
                    data={dataItemPositionSelect}
                    value={form.getValues().idItemPosition?.toString() ?? null}
                    nothingFoundMessage="Không tìm thấy dữ liệu !"
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                  <TextInput
                    label="Ghi chú"
                    placeholder="Nhập ghi chú"
                    value={form.getValues().note ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
              </Grid>
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={12}>
            <SectionCard
              icon={<IconPackages size={15} stroke={1.7} />}
              title="Thông tin sản phẩm"
              color="indigo"
            >
              <ProductWarrantyList
                dataRequestFormSelectedDetail={receiveTicketDetailModels}
                warrantyReceiveTicketDetail={warrantyReceiveTicketDetail}
                editAndSave={editAndSave}
                setEditAndSave={setEditAndSave}
                isEdit={isEdit}
                setIsEdit={setIsEdit}
                handleChangeValue={handleChangeValue}
                actionView={actionView}
                typeProduct={dataRequestFormSelected?.attribute6}
              />
            </SectionCard>
          </Grid.Col>
        </Grid>
      </Box>
      {printView === PRINT_VIEW.RETURN_CUSTOMER ? (
        <Box display={"none"}>
          <PrinterReturnCustomers
            innerRef={componentRefRC}
            dataPrint={{ ...dataPrint, ...addressBranch }}
            dataPYCXL={dataRequestFormSelected}
            isCheck={true}
          />
        </Box>
      ) : printView === PRINT_VIEW.RECEIVE_TICKET ? (
        <Box display={"none"}>
          <PrinterReceiveTicket
            innerRef={componentRef}
            dataPrint={{ ...form.getValues(), ...addressBranch }}
            dataPYCXL={dataRequestFormSelected}
          />
        </Box>
      ) : (
        <Box display={"none"}>
          <GoodsReceiptInspectionRecord
            innerRef={componentRefGRIR}
            dataPrint={{ ...form.getValues(), ...addressBranch }}
            dataPYCXL={dataRequestFormSelected}
            viewPrint={"PNHL"}
          />
        </Box>
      )}
    </>
  );
};

type DetailReceiptFormProps = {
  id?: any;
  onClose?: any;
  actionView?: any;
};

export default DetailDataView;
